import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useParams } from "react-router-dom";
import { Button, Loading, CardNotification, toast } from "@foris/avocado-suite";
import { IParams } from "@models/IParams";
import { AppContext } from "../../context/EditSessionsContext";
import { ContextApp } from "@config/Context/contextApp";
import { Types as TypeResult } from "../../context/result.reducer";
import {
  useProgrammingEditStore,
  setProgrammingRows,
  setProgrammingLoading,
  setProgrammingValidating,
  setProgrammingResults,
  applyStructureSelection,
  resetProgrammingEditStore,
} from "@context/useProgrammingEditStore";
import {
  useGetProgrammingSlots,
  useMicroprogrammingPreview,
  useProgrammingSessionsCrud,
} from "@services/collections/programming-edit/programmingEditServices";
import {
  adaptSlotsToRows,
  adaptSlotsToPreviewInput,
  adaptPreviewToChangeset,
  canPreselectStructure,
  flattenStructuredCrudResponse,
  normalizePreviewIds,
  slotsUnassignedInstructorIds,
} from "@services/collections/programming-edit/programmingEditAdapters";
import { useSettingsStore } from "@context/useSettingsStore";
import { isGroupDropDateEnabled, unassignedInstructorsInput } from "@utils/unassignedInstructors";
import { useUndoAppliedProgramming } from "../../hooks/useUndoAppliedProgramming";
import { useProgrammingLockState } from "../../hooks/useProgrammingLockState";
import ProgrammingActionBar from "../../components/programming-action-bar";
import ProgrammingSummary from "../../components/programming-summary";
import { ProgrammingValidationState } from "../../components/programming-summary/ProgrammingSummary";
import ProgrammingFilters from "../../components/programming-filters";
import ProgrammingTable from "../../components/programming-table";
import { requestLinkSessionsReload } from "../../utils/linkSessionsReload";
import css from "./programmingEdit.module.scss";

interface ProgrammingEditProps {
  /**
   * Started link with an applied structure. The view still renders its data so
   * the user can see why nothing is actionable, but every control is inert.
   */
  isReadOnly?: boolean;
}

const ProgrammingEdit: React.FC<ProgrammingEditProps> = ({ isReadOnly = false }) => {
  const { t } = useTranslation();
  const { id: linkId, scenario: scenarioId, origin: originId }: IParams = useParams();
  const { state, dispatch } = useContext(AppContext);
  const { user } = useContext(ContextApp);

  const { hasAppliedStructure } = useProgrammingLockState();
  const selectedStructureId = useProgrammingEditStore(s => s.selectedStructureId);
  const { undoAppliedProgramming, isUndoing } = useUndoAppliedProgramming();
  const originalSlots = useProgrammingEditStore(s => s.originalSlots);
  const summary = useProgrammingEditStore(s => s.summary);
  const slots = useProgrammingEditStore(s => s.slots);
  const isLoading = useProgrammingEditStore(s => s.isLoading);
  const isValidating = useProgrammingEditStore(s => s.isValidating);
  const hasChanges = useProgrammingEditStore(s => s.hasChanges);
  const stages = useProgrammingEditStore(s => s.stages);
  const isDropDateEnabled = useSettingsStore(s => isGroupDropDateEnabled(s.instructorLinkTags));
  const prevStructureIdRef = useRef<string | null>(selectedStructureId);
  const hasAttemptedPreselectRef = useRef(false);

  const canSkipCompleteness =
    user?.abilities?.can_skip_programming_stage_completeness_validation ?? false;
  const canSkipDependencies =
    user?.abilities?.can_skip_programming_stage_dependencies_validation ?? false;
  const canSkipUnscheduled =
    user?.abilities?.can_skip_unscheduled_programming_stage_validation ?? false;

  const [validationState, setValidationState] = useState<ProgrammingValidationState>({
    hasCompletenessWarnings: false,
    hasUnscheduledWarnings: false,
    hasDependencyWarnings: false,
  });

  const { getProgrammingSlots, loading: loadingSlots, slots: rawSlots } = useGetProgrammingSlots();

  const {
    getMicroprogrammingPreview,
    loading: previewing,
    previewResult,
  } = useMicroprogrammingPreview();
  const { submitCrud, loading } = useProgrammingSessionsCrud();

  /**
   * Pre-selects the structure already applied to the link when entering the view,
   * so the user does not have to re-pick it and validations run automatically.
   *
   * Runs once, only while nothing is selected yet — it must NOT seed the store
   * before mount, otherwise `prevStructureIdRef` would init to the same id and the
   * slot-fetch effect below would skip the fetch. Setting it here (with the ref
   * still `null`) lets that effect fire normally.
   *
   * Fallback (CHS-2632, Opción 2): if the applied structure is absent or has no
   * parts, do nothing — the empty selector behavior is preserved.
   */
  const appliedStructure = state?.link?.info?.programmingStructure;
  useEffect(() => {
    if (hasAttemptedPreselectRef.current) return;
    if (!appliedStructure) return; // link info not loaded yet — wait

    hasAttemptedPreselectRef.current = true;

    // Don't override an existing selection (e.g. on remount from the results view).
    if (selectedStructureId) return;

    // Empty parts → treat as "no structure assigned" and keep the empty selector.
    if (!canPreselectStructure(appliedStructure)) return;

    applyStructureSelection(appliedStructure);
  }, [appliedStructure, selectedStructureId]);

  // Fetch slots when selected structure changes
  useEffect(() => {
    if (selectedStructureId && linkId && scenarioId && originId) {
      /**
       * Skip the fetch when the structure ID hasn't actually changed.
       * This prevents re-fetching (and overwriting user edits) when the
       * component remounts after navigating back from the ResultEdit view
       * via "Volver a editar".
       */
      if (prevStructureIdRef.current === selectedStructureId) return;
      getProgrammingSlots({ scenarioId, originId, linkId });
    }
    prevStructureIdRef.current = selectedStructureId;
  }, [selectedStructureId]);

  // Store slots once API returns data (summary is computed automatically)
  useEffect(() => {
    if (rawSlots) {
      setProgrammingRows(adaptSlotsToRows(rawSlots));
    }
  }, [rawSlots]);

  // Sync loading state into store
  useEffect(() => {
    setProgrammingLoading(loadingSlots);
  }, [loadingSlots]);

  useEffect(() => {
    setProgrammingValidating(previewing);
  }, [previewing]);

  // Process preview result: build changeset → dryRun → store results → navigate
  useEffect(() => {
    if (!previewResult) return;
    if (!selectedStructureId) {
      setProgrammingValidating(false);
      return;
    }

    // If preview returned structural errors, stop here (TODO: show them in UI)
    if (previewResult.validationErrors?.length) {
      setProgrammingValidating(false);
      console.warn("[ProgrammingEdit] Preview validation errors:", previewResult.validationErrors);
      return;
    }

    const normalizedSections = normalizePreviewIds(previewResult.sections ?? []);
    const existingSectionIds = (state?.link?.sections ?? []).map((s: any) => s.id);
    // Stored with the results, so the commit sends the same unassigned instructors.
    const changeset = {
      ...adaptPreviewToChangeset(
        normalizedSections,
        state?.link?.info ?? { id: linkId },
        existingSectionIds,
        selectedStructureId,
      ),
      ...unassignedInstructorsInput(
        slotsUnassignedInstructorIds(originalSlots, slots),
        isDropDateEnabled,
      ),
    };

    submitCrud({
      scenarioId,
      originId,
      input: { ...changeset, dryRun: true, skipValidations: true },
    })
      .then(crudResult => {
        /**
         * Dispatch validation results to AppContext **before** activating the results view
         * in Zustand. This ensures `resultValidation` is already populated when
         * `SavedSectionsList` mounts, preventing a render where errors appear as "success".
         */
        const sessionsPayload = flattenStructuredCrudResponse(crudResult);
        dispatch({
          type: TypeResult.ResultValidation,
          payload: {
            sessionsPayload,
            commited: crudResult?.commited ?? false,
            skippedValidations: crudResult?.skippedValidations ?? false,
            userCanSkipValidations: crudResult?.userCanSkipValidations ?? true,
          },
        });

        /** Activate the results view — triggers `SavedSectionsList` to mount. */
        setProgrammingResults(normalizedSections, changeset);

        setProgrammingValidating(false);
      })
      .catch(() => {
        setProgrammingValidating(false);
      });
  }, [previewResult]);

  /**
   * Enables the "Validate and Apply" button when the current state is submittable.
   *
   * Checks, in order:
   * 1. All slots are assigned to a stage (`classesToAssign === 0`). Always required.
   * 2. All stages are complete — skippable via `canSkipCompleteness`.
   * 3. No dependency warnings — skippable via `canSkipDependencies`.
   *
   * **Source of truth:** the API `summary` drives the gate. The per-stage warnings in
   * `ProgrammingSummary` are informational, except `hasDependencyWarnings` (computed
   * client-side, so it has no API equivalent).
   *
   * Because of this, the summary's warning list and the button state may not match 1:1
   * on completeness — e.g. a stage with 0 blocks assigned blocks the button but shows
   * no warning, and an over-assigned stage shows a warning but the button depends on
   * how the API counts it in `completedStages`. This is intentional.
   */
  const canProceed = (() => {
    if (!hasChanges || isValidating || !summary) return false;

    // All slots must belong to a stage — no skipping this.
    if (summary.classesToAssign > 0) return false;

    // Completeness: full stage completion required unless the user can skip it.
    if (!canSkipCompleteness && summary.completedStages !== summary.totalStages) return false;

    // Unscheduled stages: every stage must have at least one block assigned unless skippable.
    if (!canSkipUnscheduled && summary.stages.some(s => s.assignedBlocks === 0)) return false;

    // Dependencies: date-order and prerequisite-assignment violations block unless skippable.
    if (!canSkipDependencies && validationState.hasDependencyWarnings) return false;

    return true;
  })();

  const handleValidateAndApply = () => {
    if (!selectedStructureId) return;

    const input = adaptSlotsToPreviewInput(slots, stages, selectedStructureId);
    getMicroprogrammingPreview({ scenarioId, originId, input });
  };

  const isUndoDisabled =
    !originalSlots.length ||
    loadingSlots ||
    isLoading ||
    loading ||
    previewing ||
    isValidating ||
    isUndoing;

  const handleConfirmUndo = async () => {
    if (isReadOnly || isUndoDisabled || !state.link.info) return;
    const result = await undoAppliedProgramming({
      scenarioId,
      originId,
      link: state.link.info,
      sectionIds: (state.link.sections ?? []).map(section => section.id),
      originalSlots,
    });
    if (result.status !== "success") {
      if (result.reason !== "in-progress") {
        // Every reason shares the same toast; the log tells them apart.
        console.warn("[ProgrammingEdit] Undo applied programming failed:", result.reason);
        toast.error(t("programming-edit.undo.error-toast"));
      }
      return;
    }
    toast.success(t("programming-edit.undo.success-toast"));
    resetProgrammingEditStore();
    requestLinkSessionsReload();
  };

  // Reiniciar clears local selection and edits. Restore only the saved rows so
  // undo remains available without selecting another structure.
  const handleReset = () => {
    if (rawSlots) setProgrammingRows(adaptSlotsToRows(rawSlots));
    else if (originalSlots.length) setProgrammingRows(originalSlots);
  };

  const componentOptions = useMemo(() => {
    const unique = Array.from(new Set(slots.map(s => s.component).filter(Boolean)));
    return unique.map(c => ({ value: c, label: c }));
  }, [slots]);

  return (
    <div className={cx(css.container, css.programmingEdit)}>
      <ProgrammingActionBar
        isReadOnly={isReadOnly || isUndoing}
        isUndoDisabled={isUndoDisabled}
        onConfirmUndo={handleConfirmUndo}
        onReset={handleReset}
      />

      <div className={css.programmingEdit_content}>
        {(isLoading || loading || previewing || isValidating || isUndoing) && <Loading />}

        {/*
          Exactly one banner at a time. The started one wins because it
          describes the more restrictive state; the override ability keeps a
          started link on the warning banner, since the undo is still available.
        */}
        {hasAppliedStructure && (
          <div className={css.programmingEdit_banner}>
            <CardNotification
              title={t(
                isReadOnly
                  ? "programming-edit.banners.started.title"
                  : "programming-edit.banners.applied.title",
              )}
              state={isReadOnly ? "error" : "warning"}
              outlined
            >
              {t(
                isReadOnly
                  ? "programming-edit.banners.started.description"
                  : "programming-edit.banners.applied.description",
              )}
            </CardNotification>
          </div>
        )}

        {!isLoading && !selectedStructureId && (
          <div className={css.programmingEdit_emptyState}>
            <CardNotification title={t("programming-edit.empty-state.title")} state="info" outlined>
              {t("programming-edit.empty-state.description")}
            </CardNotification>
          </div>
        )}

        {selectedStructureId && !isLoading && (
          <>
            <div className={css.topActions}>
              <Button
                variant="secondary"
                onClick={handleValidateAndApply}
                disabled={!canProceed || isReadOnly || isUndoing}
                className={css.applyBtn}
              >
                {isValidating
                  ? t("programming-edit.actions.validating")
                  : t("programming-edit.actions.validate-and-apply")}
              </Button>
            </div>

            {summary && (
              <ProgrammingSummary
                summary={summary}
                canSkipCompleteness={canSkipCompleteness}
                canSkipUnscheduled={canSkipUnscheduled}
                onValidationState={setValidationState}
              />
            )}

            <ProgrammingFilters componentOptions={componentOptions} />

            <ProgrammingTable isReadOnly={isReadOnly || isUndoing} />
          </>
        )}
      </div>
    </div>
  );
};

export default ProgrammingEdit;
